import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/icons';
import { ErrorBoundary } from '@/components/error';
import { Modal } from '@/components/ui/Modal';
import { AssistFab } from '@/components/shared/AssistFab';
import { useSession, useAppContext } from '@/hooks';
import { isInstitutionAdmin } from '@/auth/session';
import { CommandPalette, type PaletteMode } from '@/components/command/CommandPalette';
import { destinationsFor } from '@/components/command/destinations';
import { useCommandHistory } from '@/hooks/useCommandHistory';
import styles from './InstitutionAdminLayout.module.css';

interface AdminNavItem {
  to: string;
  icon: IconName;
  labelKey: string;
  end?: boolean;
}

interface AdminNavGroup {
  labelKey: string;
  items: readonly AdminNavItem[];
}

const NAV_GROUPS: readonly AdminNavGroup[] = [
  {
    labelKey: 'admin.nav.group.overview',
    items: [
      { to: '/admin/institution', icon: 'pulse', labelKey: 'admin.nav.dashboard', end: true },
    ],
  },
  {
    labelKey: 'admin.nav.group.institution',
    items: [
      { to: '/admin/institution/profile', icon: 'profile', labelKey: 'admin.nav.profile' },
      { to: '/admin/institution/campuses', icon: 'campus', labelKey: 'admin.nav.campuses' },
      { to: '/admin/institution/academic-years', icon: 'academic', labelKey: 'admin.nav.academicYears' },
      { to: '/admin/institution/academic-structure', icon: 'academic', labelKey: 'admin.nav.academicStructure' },
    ],
  },
  {
    labelKey: 'admin.nav.group.users',
    items: [
      { to: '/admin/institution/users', icon: 'users', labelKey: 'admin.nav.users' },
      { to: '/admin/institution/roles', icon: 'role', labelKey: 'admin.nav.roles' },
      { to: '/admin/institution/credentials', icon: 'role', labelKey: 'admin.nav.credentials' },
    ],
  },
  {
    labelKey: 'admin.nav.group.configuration',
    items: [
      { to: '/admin/institution/products', icon: 'products', labelKey: 'admin.nav.products' },
      { to: '/admin/institution/branding', icon: 'branding', labelKey: 'admin.nav.branding' },
      { to: '/admin/institution/integrations', icon: 'integration', labelKey: 'admin.nav.integrations' },
      { to: '/admin/institution/experience', icon: 'globe', labelKey: 'admin.nav.experience' },
      { to: '/admin/institution/services', icon: 'settings', labelKey: 'admin.nav.services' },
      { to: '/admin/institution/form-schemas', icon: 'checklist', labelKey: 'admin.nav.formSchemas' },
      { to: '/admin/institution/workflows', icon: 'checklist', labelKey: 'admin.nav.workflows' },
      { to: '/admin/institution/data-import', icon: 'plus', labelKey: 'admin.nav.dataImport' },
      { to: '/admin/institution/marketplace', icon: 'products', labelKey: 'admin.nav.marketplace' },
      { to: '/admin/institution/parent-access', icon: 'parent', labelKey: 'admin.nav.parentAccess' },
    ],
  },
  {
    labelKey: 'admin.nav.group.operations',
    items: [
      { to: '/admin/institution/tasks', icon: 'tasks', labelKey: 'admin.nav.tasks' },
      { to: '/admin/institution/reports', icon: 'report', labelKey: 'admin.nav.reports' },
      { to: '/admin/institution/announcements', icon: 'announcement', labelKey: 'admin.nav.announcements' },
      { to: '/admin/institution/audit', icon: 'history', labelKey: 'admin.nav.audit' },
      { to: '/admin/institution/security', icon: 'warning', labelKey: 'admin.nav.security' },
      { to: '/admin/institution/support', icon: 'mail', labelKey: 'admin.nav.support' },
    ],
  },
];

export function InstitutionAdminLayout() {
  const { t, i18n } = useTranslation();
  const { status, session, login, logout } = useSession();
  const { activeContext } = useAppContext();
  const [railOpen, setRailOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [palette, setPalette] = useState<PaletteMode | null>(null);
  const location = useLocation();

  useEffect(() => setRailOpen(false), [location.pathname]);

  // The same role-scoped catalogue as the workspace palette, so a feature that cannot be
  // opened also cannot be offered — a result that ends in a permission error is worse than
  // none, and search inside the console must never reach past it.
  const destinations = useMemo(
    () =>
      destinationsFor({
        roleCode: activeContext?.role?.code,
        realmRoles: session?.realmRoles,
      }),
    [activeContext?.role?.code, session?.realmRoles],
  );

  const allowedIds = useMemo(() => destinations.map((d) => d.id), [destinations]);

  const { recent, favourites, remember, toggleFavourite } = useCommandHistory({
    subject: session?.identityId,
    institutionId: activeContext?.institution?.id,
    roleCode: activeContext?.role?.code,
    allowedIds,
  });

  // Ctrl/Cmd+K from anywhere in the console, plus the button in the topbar for the people
  // who have not been told the shortcut. The console is broad; jumping by name is the fast
  // way through it.
  const openSearch = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setPalette('search');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', openSearch);
    return () => window.removeEventListener('keydown', openSearch);
  }, [openSearch]);

  // Browser tab / history titles: "Users · Sacred Heart College · CampusOne".
  // Without this, every admin tab reads the same and history is unusable.
  useEffect(() => {
    const items = NAV_GROUPS.flatMap((group) => group.items);
    const match =
      items.find((item) => item.end && location.pathname === item.to) ??
      // Longest prefix wins so /users doesn't also match the dashboard root.
      [...items]
        .filter((item) => !item.end && location.pathname.startsWith(item.to))
        .sort((a, b) => b.to.length - a.to.length)[0];
    const parts = [
      match ? t(match.labelKey) : undefined,
      activeContext?.institution?.name,
      'CampusOne',
    ].filter(Boolean);
    document.title = parts.join(' · ');
  }, [location.pathname, activeContext?.institution?.name, t]);

  if (status === 'loading') {
    return (
      <div className={styles.gate} role="status" aria-live="polite" aria-label={t('common.loading')}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className={styles.gate}>
        <div className={styles.gateCard}>
          <p className={styles.gateEyebrow}>{t('admin.shell.eyebrow')}</p>
          <h1 className={styles.gateTitle}>{t('admin.gate.signInTitle')}</h1>
          <p className={styles.gateBody}>{t('admin.gate.signInBody')}</p>
          <button type="button" className={styles.gateCta} onClick={login}>
            {t('auth.signIn')}
          </button>
        </div>
      </div>
    );
  }

  if (!isInstitutionAdmin(activeContext?.role?.code)) {
    return (
      <div className={styles.gate}>
        <div className={styles.gateCard} role="status">
          <p className={styles.gateEyebrow}>{t('admin.shell.eyebrow')}</p>
          <h1 className={styles.gateTitle}>{t('admin.gate.deniedTitle')}</h1>
          <p className={styles.gateBody}>{t('admin.gate.deniedBody')}</p>
          <Link className={styles.gateCta} to="/">
            {t('admin.gate.backToWorkspace')}
          </Link>
        </div>
      </div>
    );
  }

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');
  const institutionName = activeContext?.institution?.name || '';

  return (
    <div className={styles.console}>
      <a className={styles.skipLink} href="#admin-institution-content">
        {t('admin.shell.skipToContent')}
      </a>

      {railOpen && <div className={styles.railOverlay} role="presentation" onClick={() => setRailOpen(false)} />}

      <aside
        className={`${styles.rail} ${railOpen ? styles.railOpen : ''}`}
        aria-label={t('admin.shell.navLabel')}
      >
        <div className={styles.railHeader}>
          <span className={styles.railMark} aria-hidden="true">
            <Icon name="admin" size={20} />
          </span>
          <span className={styles.railWordmark}>
            <strong>{t('admin.shell.wordmark')}</strong>
            <span>{t('admin.shell.wordmarkSub')}</span>
          </span>
          <button
            type="button"
            className={styles.railClose}
            onClick={() => setRailOpen(false)}
            aria-label={t('common.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <nav className={styles.railNav} aria-label={t('admin.shell.navLabel')}>
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className={styles.railGroup}>
              <h2 className={styles.railGroupLabel}>{t(group.labelKey)}</h2>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `${styles.railLink} ${isActive ? styles.railLinkActive : ''}`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.railFooter}>
          <Link className={styles.railFooterBtn} to="/">
            <Icon name="switch" size={16} />
            {t('admin.shell.exitToWorkspace')}
          </Link>
          <button type="button" className={styles.railFooterBtn} onClick={toggleLanguage}>
            <Icon name="globe" size={16} />
            {i18n.language === 'en' ? 'Français' : 'English'}
          </button>
          <button
            type="button"
            className={`${styles.railFooterBtn} ${styles.railFooterBtnDanger}`}
            onClick={() => setSignOutOpen(true)}
          >
            <Icon name="logout" size={16} />
            {t('auth.signOut')}
          </button>
        </div>
      </aside>

      <div className={styles.body}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setRailOpen(true)}
            aria-label={t('common.menu')}
          >
            <Icon name="menu" size={22} />
          </button>
          <div className={styles.topbarIdentity}>
            <span className={styles.topbarEyebrow}>{institutionName}</span>
            <span className={styles.topbarOperator}>{session?.name || session?.email || ''}</span>
          </div>
          <button
            type="button"
            className={styles.topbarSearch}
            onClick={() => setPalette('search')}
            aria-label={t('command.search')}
          >
            <Icon name="search" size={16} />
            <span>{t('command.search')}</span>
            <kbd className={styles.topbarSearchHint} aria-hidden="true">
              Ctrl K
            </kbd>
          </button>
          <span className={styles.operatorBadge}>{t('admin.shell.roleBadge')}</span>
        </header>

        <div className={styles.scopeBar} role="note">
          <Icon name="warning" size={16} />
          <span className={styles.scopeText}>
            <strong>{t('admin.shell.scopeTitle')}</strong>{' '}
            {t('admin.shell.scopeBody', { institutionName })}
          </span>
        </div>

        <main className={styles.main} id="admin-institution-content" tabIndex={-1}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {palette !== null && (
        <CommandPalette
          open
          mode={palette ?? 'search'}
          destinations={destinations}
          onClose={() => setPalette(null)}
          recent={recent}
          favourites={favourites}
          onVisit={remember}
          onToggleFavourite={toggleFavourite}
        />
      )}

      <Modal
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        title={t('auth.signOutTitle')}
        description={t('auth.signOutDescription')}
      >
        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={() => setSignOutOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="modal-button modal-button--danger"
            onClick={() => {
              setSignOutOpen(false);
              logout();
            }}
          >
            {t('auth.signOut')}
          </button>
        </div>
      </Modal>
      <AssistFab />
    </div>
  );
}
